import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { getAllDimensionsFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getLevelsByColumnValueForDimensionFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getAllLevelsForDimensionFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getAllPrivilegedLevelsFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getAllUnprivilegedLevelsFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getDimensionByColumnHeaderValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getPrivilegedLevelForDimensionFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { isMinorityDimensionLevelFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getLevelByColumnValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getAllLevelsFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { UseDimensionConfigPayload } from 'libs/end-to-end/src/use-dimension-config-payload';

export const useDimensionConfig = (): UseDimensionConfigPayload => {
  const { myObservation } = useObservation();

  const allDimensions: DimensionDefinition[] = useMemo(() => {
    return getAllDimensionsFromObservation(myObservation);
  }, [myObservation]);

  const allLevels: LevelDefinition[] = useMemo(() => {
    return getAllLevelsFromObservation(myObservation);
  }, [myObservation]);

  const allPrivilegedLevels: LevelDefinition[] = useMemo(() => {
    return getAllPrivilegedLevelsFromObservation(myObservation);
  }, [myObservation]);

  const allUnprivilegedLevels: LevelDefinition[] = useMemo(() => {
    return getAllUnprivilegedLevelsFromObservation(myObservation);
  }, [myObservation]);

  /**
   * Returns a dimension definition for a given column header value
   * @param columnHeaderValue - the columnHeaderValue of the dimension
   * @returns a dimension definition
   */
  const getDimensionByColumnHeaderValue = useCallback(
    (columnHeaderValue: string) => {
      return getDimensionByColumnHeaderValueFromObservation(
        myObservation,
        columnHeaderValue,
      );
    },
    [myObservation],
  );

  /**
   * Returns a level definition for a given column value and column header value
   * @param columnHeaderValue - the columnHeaderValue of the dimension
   * @param columnValue - the columnValue of the level
   * @returns a level definition
   * @example
   * const level = getLevelByColumnValue('Race', 'Other');
   */
  const getLevelByColumnValue = useCallback(
    (columnHeaderValue: string, columnValue: string) => {
      return getLevelByColumnValueFromObservation(
        myObservation,
        columnHeaderValue,
        columnValue,
      );
    },
    [myObservation],
  );

  /**
   * Returns all levels for a given dimension
   * @param columnHeaderValue - the columnHeaderValue of the dimension
   * @returns an array of level definitions
   * @example
   * const levels = getAllLevelsForDimension('age');
   */
  const getAllLevelsForDimension = useCallback(
    (columnHeaderValue: string) => {
      return getAllLevelsForDimensionFromObservation(
        myObservation,
        columnHeaderValue,
      );
    },
    [myObservation],
  );

  /**
   * Returns all privileged levels for a given dimension
   * @param columnHeaderValue - the columnHeaderValue of the dimension
   * @returns the first privileged level definitions
   * @example
   * const levels = getPrivilegedLevelForDimension('age');
   */
  const getPrivilegedLevelForDimension = useCallback(
    (columnHeaderValue: string) => {
      return getPrivilegedLevelForDimensionFromObservation(
        myObservation,
        columnHeaderValue,
      );
    },
    [myObservation],
  );

  /**
   * Returns whether a level is a minority level
   * if the level is not found, it is not a minority level
   * @param columnHeaderValue - the columnHeaderValue of the dimension
   * @param columnValue - the columnValue of the level
   * @returns a boolean
   */
  const isMinorityDimensionLevel = useCallback(
    (columnHeaderValue: string, columnValue: string) => {
      return isMinorityDimensionLevelFromObservation(
        myObservation,
        columnHeaderValue,
        columnValue,
      );
    },
    [myObservation],
  );

  const getLevelsByColumnValueForDimension = useCallback(
    (dimension: string) => {
      return getLevelsByColumnValueForDimensionFromObservation(
        myObservation,
        dimension,
      );
    },
    [myObservation],
  );

  const getDimensionByLevelColumnValue = useCallback(
    (levelColumnValue: string) => {
      return allDimensions.find((d) =>
        d.levels.some((l) => l.columnValue === levelColumnValue),
      );
    },
    [allDimensions],
  );

  return {
    allDimensions,
    allLevels,
    allPrivilegedLevels,
    allUnprivilegedLevels,
    getAllLevelsForDimension,
    getLevelByColumnValue,
    getDimensionByColumnHeaderValue,
    getPrivilegedLevelForDimension,
    isMinorityDimensionLevel,
    getLevelsByColumnValueForDimension,
    getDimensionByLevelColumnValue,
  };
};
